import { todayLocalIso } from '@core/dates';
import { formatDuration } from '@core/duration';
import { DEFAULT_PLANNER_PARAMS, type PlanResult, generatePlan } from '@core/planner';
import { sessionActions } from '@core/store';
import { Modal } from '@ui/components/Modal';
import { toast } from '@ui/components/Toast';
import { useStore } from '@ui/lib/useStore';
import { useState } from 'preact/hooks';

const DOW_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export function PlanModal(props: { open: boolean; onClose: () => void }) {
  const contents = useStore((s) => s.contents);
  const goals = useStore((s) => s.goals);
  const sessions = useStore((s) => s.sessions);
  const routines = useStore((s) => s.routines);
  const settings = useStore((s) => s.settings);

  const activeGoal =
    goals.find((g) => g.id === settings.active_goal_id) ||
    goals.find((g) => g.priority === 1 && g.status === 'active');

  const [studyDays, setStudyDays] = useState<number[]>(DEFAULT_PLANNER_PARAMS.study_days);
  const [hoursPerDay, setHoursPerDay] = useState(DEFAULT_PLANNER_PARAMS.hours_per_day);
  const [blockMin, setBlockMin] = useState(settings.pomodoro_focus_min);
  const [startTime, setStartTime] = useState(DEFAULT_PLANNER_PARAMS.start_time);
  const [preview, setPreview] = useState<PlanResult | null>(null);

  const pendingCount = contents.filter((c) => c.status === 'todo' || c.status === 'doing').length;
  const plannedCount = sessions.filter((s) => s.status === 'planned').length;

  function toggleDay(d: number) {
    setStudyDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
    setPreview(null);
  }

  function runPreview() {
    if (studyDays.length === 0) {
      toast('Escolha pelo menos 1 dia de estudo', 'err');
      return;
    }
    const result = generatePlan(
      contents,
      {
        start_date: todayLocalIso(),
        study_days: studyDays,
        hours_per_day: hoursPerDay,
        block_min: blockMin,
        break_min: 10,
        start_time: startTime,
      },
      activeGoal?.target_date?.slice(0, 10),
      routines,
    );
    setPreview(result);
  }

  function applyPlan() {
    if (!preview) return;
    const removed = sessionActions.clearPlanned();
    const added = sessionActions.bulkCreate(preview.sessions);
    toast(
      `✓ Plano gerado: ${added} sessões` +
        (removed > 0 ? ` (${removed} antigas substituídas)` : ''),
      'ok',
      4000,
    );
    setPreview(null);
    props.onClose();
  }

  const viabColor =
    preview?.diagnostic.viability === 'ok'
      ? 'var(--green)'
      : preview?.diagnostic.viability === 'ajustado'
        ? 'var(--amber)'
        : preview?.diagnostic.viability === 'inviavel'
          ? 'var(--red)'
          : 'var(--text-dim)';

  const viabLabel =
    preview?.diagnostic.viability === 'ok'
      ? '✓ VIÁVEL COM FOLGA'
      : preview?.diagnostic.viability === 'ajustado'
        ? '⚠ APERTADO (margem < 20%)'
        : preview?.diagnostic.viability === 'inviavel'
          ? '✗ NÃO CABE NO PRAZO'
          : 'SEM PRAZO DEFINIDO';

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      title="Gerar plano de estudo"
      maxWidth={560}
      actions={
        <>
          <button class="btn" onClick={props.onClose}>
            CANCELAR
          </button>
          {!preview && (
            <button class="btn primary" onClick={runPreview} disabled={pendingCount === 0}>
              ▸ SIMULAR
            </button>
          )}
          {preview && (
            <button class="btn green" onClick={applyPlan}>
              ✓ APLICAR PLANO
            </button>
          )}
        </>
      }
    >
      {pendingCount === 0 ? (
        <div class="empty-state" style={{ padding: '16px 0' }}>
          Nenhum conteúdo pendente. Adicione conteúdos primeiro.
        </div>
      ) : (
        <>
          <div
            style={{
              fontFamily: 'var(--f-mono)',
              fontSize: '11.5px',
              color: 'var(--text-soft)',
              marginBottom: '16px',
              lineHeight: 1.6,
            }}
          >
            {pendingCount} conteúdo(s) pendente(s)
            {activeGoal?.target_date &&
              ` · prova em ${new Date(activeGoal.target_date).toLocaleDateString('pt-BR')}`}
            {plannedCount > 0 && (
              <div style={{ color: 'var(--amber)' }}>
                ⚠ {plannedCount} sessões planejadas serão substituídas (concluídas ficam)
              </div>
            )}
          </div>

          <div class="field">
            <label>Dias de estudo</label>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {DOW_LABELS.map((lbl, d) => (
                <button
                  key={lbl}
                  type="button"
                  class={`btn sm ${studyDays.includes(d) ? 'primary' : 'ghost'}`}
                  onClick={() => toggleDay(d)}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
            <div class="field">
              <label>Horas/dia</label>
              <input
                class="input"
                type="number"
                min="0.5"
                max="12"
                step="0.5"
                value={hoursPerDay}
                onInput={(e) => {
                  setHoursPerDay(Number.parseFloat((e.target as HTMLInputElement).value) || 2);
                  setPreview(null);
                }}
              />
            </div>
            <div class="field">
              <label>Bloco (min)</label>
              <input
                class="input"
                type="number"
                min="10"
                max="90"
                step="5"
                value={blockMin}
                onInput={(e) => {
                  setBlockMin(Number.parseInt((e.target as HTMLInputElement).value, 10) || 25);
                  setPreview(null);
                }}
              />
            </div>
            <div class="field">
              <label>Início</label>
              <input
                class="input"
                type="time"
                value={startTime}
                onInput={(e) => {
                  setStartTime((e.target as HTMLInputElement).value || '19:00');
                  setPreview(null);
                }}
              />
            </div>
          </div>

          {preview && (
            <div
              style={{
                marginTop: '14px',
                padding: '16px',
                border: `1px solid ${viabColor}`,
                borderRadius: 'var(--r-md)',
                background: 'rgba(0,8,24,0.5)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--f-heading)',
                  fontSize: '13px',
                  letterSpacing: '1.5px',
                  color: viabColor,
                  marginBottom: '10px',
                }}
              >
                {viabLabel}
              </div>
              <div
                style={{
                  fontFamily: 'var(--f-mono)',
                  fontSize: '12px',
                  color: 'var(--text)',
                  lineHeight: 1.8,
                }}
              >
                ▸ {preview.sessions.length} sessões · {formatDuration(preview.diagnostic.total_min)}{' '}
                total
                <br />▸ ~{preview.diagnostic.weeks_needed} semana(s) de estudo
                {preview.diagnostic.end_date && (
                  <>
                    <br />▸ término previsto:{' '}
                    {new Date(`${preview.diagnostic.end_date}T12:00:00`).toLocaleDateString(
                      'pt-BR',
                    )}
                  </>
                )}
                {preview.diagnostic.margin !== null && (
                  <>
                    <br />▸ margem de folga: {(preview.diagnostic.margin * 100).toFixed(0)}%
                  </>
                )}
                {preview.diagnostic.unplaced_contents > 0 && (
                  <div style={{ color: 'var(--red)' }}>
                    ⚠ {preview.diagnostic.unplaced_contents} conteúdo(s) não couberam no horizonte
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
